/*
 * File: AssignedRule.tsx
 * Project: beaker
 * -----
 * Last Modified: 27 Apr 2022 11:11:11 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Card, Col, Dropdown, Menu, message, Row, Space } from 'antd';
import { LogoUpload } from 'components/molecules/LogoUpload';
import { UserForm } from 'components/molecules/UserForm';
import { AntPageTitle } from 'components/UI/AntPageTitle';
import { FormikProvider, useFormik } from 'formik';
import Cookies from 'js-cookie';
import { EditProfile } from 'models/Users';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpdateUserMutation } from 'redux/services/cookieMonster/cookieMonsterApi';
import { setUser } from 'redux/slices/appSlice';
import { useAppDispatch, useAppSelector } from 'redux/store';

export const EditProfilePage: React.FC = () => {
  /* ******************** Hooks ******************** */
  const navigate = useNavigate();

  const [updateUser] = useUpdateUserMutation();
  const dispatch = useAppDispatch();
  const { user: reduxUser } = useAppSelector((state) => state.app);

  const { user } = useAuth0();

  const profileId = user?.sub as string;

  const customerCookie = Cookies.get(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`);

  /* ******************** API Hooks ******************** */

  const formik = useFormik<EditProfile>({
    enableReinitialize: true,
    initialValues: {
      email: user ? user.email : '',
      givenName: user ? user.given_name : '',
      familyName: user ? user.family_name : '',
      phoneNumber: user ? user['https://acuity.mdsiinc.com/user/user_metadata'].phone_number : ''
    },
    onSubmit: async (values) => {
      if (!user) {
        message.error("User doesn't exist");

        return;
      }
      try {
        const {
          given_name,
          user_metadata: { phone_number },
          family_name,
          email,
          name
        } = await updateUser({ userId: profileId, payload: { ...user, ...values } }).unwrap();

        dispatch(setUser({ ...reduxUser, ...{ given_name, family_name, phone_number: typeof phone_number === 'string' ? phone_number : undefined, email, name } }));
        message.success(`User ${name} was successfully updated!`);
      } catch (error) {
        console.log(error);
        message.error('User failed to be updated!');
      }
    }
  });

  /* ******************** Functions ******************** */

  const handleClose = (): void => {
    if (customerCookie) navigate(`/divisions`);
    else navigate(`/`);
  };

  const handleSave = async (close?: boolean): Promise<void> => {
    await formik.submitForm();

    if (close) {
      handleClose();
    }
  };
  const menuJSX = (
    <Menu disabledOverflow={true}>
      <Menu.Item key="1" onClick={(): Promise<void> => handleSave(false)}>
        Save
      </Menu.Item>
    </Menu>
  );

  return (
    <Space direction="vertical">
      <Col style={{ padding: 10, background: 'rgb(206, 213, 242)' }}>
        <Row justify="space-between">
          <AntPageTitle text={`Edit Profile`} />
          <Space>
            <Button onClick={handleClose} icon={<ArrowLeftOutlined />}>
              Back
            </Button>
            <Dropdown.Button overlay={menuJSX} onClick={(): Promise<void> => handleSave(true)} loading={formik.isSubmitting} type="primary" icon={<SaveOutlined />}>
              Save and Exit
            </Dropdown.Button>
          </Space>
        </Row>
      </Col>
      <Card>
        <FormikProvider value={formik}>
          <UserForm />
        </FormikProvider>
      </Card>
      <Card>
        <LogoUpload profileData={user} />
      </Card>
    </Space>
  );
};
